import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Heart, Sparkles, Users, Mail, Facebook } from "lucide-react";
import { LANDING } from "@/constants/testIds";
import { HERO_BANNER } from "@/data/marketplace";
import { SignupModal } from "@/components/SignupModal";
import { useAuth } from "@/context/AuthContext";

const HERO_IMG = HERO_BANNER;

export default function Landing() {
  const [openAuth, setOpenAuth] = useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();

  const handleStart = () => {
    if (user) navigate("/onboarding");
    else setOpenAuth(true);
  };

  return (
    <div data-testid={LANDING.hero} className="relative">
      <SignupModal open={openAuth} onOpenChange={setOpenAuth} />
      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="mx-auto max-w-6xl px-6 md:px-10 pt-14 md:pt-24 pb-16 md:pb-28 grid md:grid-cols-12 gap-10 md:gap-16 items-center">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            className="md:col-span-7 order-2 md:order-1"
          >
            <span className="inline-block text-xs tracking-[0.25em] uppercase text-[#5C6B62] mb-6">
              A registry · for her
            </span>
            <h1 className="font-display text-4xl sm:text-5xl md:text-6xl lg:text-[4.25rem] leading-[1.05] text-[#2D3732]">
              The baby has a registry.
              <br />
              <span className="italic text-[#D98A71]">Where's hers?</span>
            </h1>
            <p
              data-testid={LANDING.coreMessage}
              className="mt-8 text-base md:text-lg text-[#5C6B62] leading-relaxed max-w-xl"
            >
              Held helps friends and family give the support a new mum actually
              needs — built for the mother's recovery, not the nursery.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <button
                type="button"
                onClick={handleStart}
                data-testid={LANDING.ctaPrimary}
                className="group rounded-full bg-[#D98A71] hover:bg-[#c67a63] text-[#FDFBF7] px-7 py-3.5 text-sm font-medium transition-colors inline-flex items-center gap-2"
              >
                Build her registry
                <span className="transition-transform group-hover:translate-x-1">→</span>
              </button>
              <a
                href="#how"
                data-testid={LANDING.ctaSecondary}
                className="rounded-full border border-[#E5E1D8] hover:border-[#2D3732] text-[#2D3732] px-7 py-3.5 text-sm font-medium transition-colors"
              >
                How it works
              </a>
            </div>

            <div className="mt-14 flex items-center gap-8 text-xs tracking-[0.15em] uppercase text-[#5C6B62]">
              <span>Aotearoa · New Zealand</span>
              <span className="hidden sm:inline w-8 h-px bg-[#E5E1D8]" />
              <span className="hidden sm:inline">Est. 2026</span>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
            className="md:col-span-5 order-1 md:order-2 relative"
          >
            <div className="relative aspect-[4/5] rounded-[2rem] overflow-hidden">
              <img
                src={HERO_IMG}
                alt="A mother taking a quiet moment for herself"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#2D3732]/25 via-transparent to-transparent" />
            </div>
            <div className="hidden md:block absolute -bottom-6 -left-8 bg-[#FDFBF7] border border-[#E5E1D8] rounded-2xl px-5 py-4 shadow-[0_20px_40px_-24px_rgba(45,55,50,0.2)]">
              <p className="text-xs tracking-[0.2em] uppercase text-[#5C6B62]">Now funding</p>
              <p className="font-display text-lg text-[#2D3732] mt-1">Two weeks of meals</p>
              <div className="mt-3 w-40 h-1.5 rounded-full bg-[#F4F1EA] overflow-hidden">
                <div className="h-full w-3/4 rounded-full bg-[#A3B19B]" />
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* How it works */}
      <section id="how" className="bg-[#F4F1EA]/60 border-y border-[#E5E1D8]">
        <div className="mx-auto max-w-6xl px-6 md:px-10 py-20 md:py-28">
          <div className="max-w-2xl">
            <span className="text-xs tracking-[0.25em] uppercase text-[#5C6B62]">
              How Held works
            </span>
            <h2 className="font-display text-3xl md:text-5xl text-[#2D3732] mt-4 leading-tight">
              Three quiet steps.
              <br />
              <span className="italic text-[#A3B19B]">Care that arrives.</span>
            </h2>
          </div>

          <div className="mt-14 grid md:grid-cols-3 gap-6 md:gap-8">
            {[
              {
                icon: Heart,
                title: "Share what you need",
                body: "A gentle three-question check-in. Due date, city, priorities.",
              },
              {
                icon: Sparkles,
                title: "Curate her registry",
                body: "Add local meals, physio, doulas, and pampering — chosen by you.",
              },
              {
                icon: Users,
                title: "Loved ones contribute",
                body: "A shareable list. Friends and family fund what she really needs.",
              },
            ].map(({ icon: Icon, title, body }, i) => (
              <div
                key={title}
                className="bg-[#FDFBF7] border border-[#E5E1D8] rounded-3xl p-8 card-lift"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-[#F4F1EA] border border-[#E5E1D8] flex items-center justify-center">
                    <Icon size={18} strokeWidth={1.5} className="text-[#D98A71]" />
                  </div>
                  <span className="text-xs tracking-[0.2em] uppercase text-[#5C6B62]">
                    0{i + 1}
                  </span>
                </div>
                <h3 className="font-display text-2xl text-[#2D3732] mt-6">{title}</h3>
                <p className="text-sm text-[#5C6B62] leading-relaxed mt-3">{body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Quote */}
      <section className="mx-auto max-w-4xl px-6 md:px-10 py-24 md:py-32 text-center">
        <p className="font-display text-2xl md:text-4xl text-[#2D3732] leading-[1.3] italic">
          "The nursery is ready. The bassinet is beautiful.
          <br />
          But nobody thought to fill the fridge for her."
        </p>
        <p className="mt-8 text-xs tracking-[0.25em] uppercase text-[#5C6B62]">
          — every mother, everywhere
        </p>
      </section>

      <footer className="border-t border-[#E5E1D8]">
        <div className="mx-auto max-w-6xl px-6 md:px-10 py-14 grid gap-10 md:grid-cols-3 items-start">
          <div>
            <p className="font-display text-2xl text-[#2D3732]">
              held<span className="text-[#D98A71]">.</span>
            </p>
            <p className="mt-3 text-sm text-[#5C6B62] leading-relaxed max-w-xs">
              A registry for the mother — not the nursery. Made in Ōtautahi, Aotearoa.
            </p>
          </div>
          <div>
            <p className="text-[10px] tracking-[0.25em] uppercase text-[#5C6B62]">Support</p>
            <p className="mt-3 text-sm text-[#2D3732]">
              Need help? Drop us a message anytime! 💛
            </p>
            <a
              href="mailto:H3inclusive@gmail.com"
              data-testid="footer-contact"
              className="mt-4 inline-flex items-center gap-2 text-sm text-[#2D3732] hover:text-[#D98A71] transition-colors"
            >
              <Mail size={14} strokeWidth={1.5} />
              Contact Support
            </a>
          </div>
          <div>
            <p className="text-[10px] tracking-[0.25em] uppercase text-[#5C6B62]">Follow</p>
            <a
              href="https://facebook.com/held4her"
              target="_blank"
              rel="noreferrer"
              data-testid="footer-facebook"
              className="mt-3 inline-flex items-center gap-2 text-sm text-[#2D3732] hover:text-[#D98A71] transition-colors"
            >
              <Facebook size={16} strokeWidth={1.5} />
              held4her
            </a>
          </div>
        </div>
        <div className="border-t border-[#E5E1D8] py-6 text-center text-xs tracking-[0.2em] uppercase text-[#5C6B62]">
          Held · Made in Ōtautahi, for mothers everywhere
        </div>
      </footer>
    </div>
  );
}
